import { InboxOutlined } from '@ant-design/icons'
import { Form, Input, Modal, notification, Space, Upload } from 'antd'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'
import { useState } from 'react'
import {
  MCP_MOCK_UPLOAD_MAX_BYTES,
  buildMockUpdateInput,
  deriveMockName,
  McpMock,
  parseMockFile,
  validateMockUpload,
} from './mcpMocksAdminModel'
import {
  useCreateMcpMockMutation,
  useUpdateMcpMockMutation,
} from './mcpMocksAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
  /** When provided the modal edits this mock; otherwise it creates a new one. */
  mock?: McpMock
}

const McpMockUploadModal = ({ open, onClose, mock }: Props) => {
  const isEdit = mock != null
  // In edit mode the existing config seeds the form, so the admin can save a
  // rename or drop a replacement .json; the dropzone overwrites the body.
  const [name, setName] = useState(mock?.name ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [body, setBody] = useState<Record<string, unknown> | null>(
    mock?.body ?? null,
  )
  const createMutation = useCreateMcpMockMutation()
  const updateMutation = useUpdateMcpMockMutation()
  const mutation = isEdit ? updateMutation : createMutation

  const reset = () => {
    setName(mock?.name ?? '')
    setFile(null)
    setBody(mock?.body ?? null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const uploadProps: UploadProps = {
    accept: '.json,application/json',
    multiple: false,
    maxCount: 1,
    fileList: file
      ? [{ uid: '-1', name: file.name, status: 'done', size: file.size } as UploadFile]
      : [],
    beforeUpload: async (selected) => {
      if (selected.size > MCP_MOCK_UPLOAD_MAX_BYTES) {
        notification.error({
          message: 'File too large',
          description: `Maximum upload size is ${MCP_MOCK_UPLOAD_MAX_BYTES} bytes (1 MiB).`,
        })
        return Upload.LIST_IGNORE
      }
      const { body: parsed, error } = parseMockFile(await selected.text())
      if (error || !parsed) {
        notification.error({ message: 'Could not read file', description: error })
        return Upload.LIST_IGNORE
      }
      setFile(selected)
      setBody(parsed)
      // On create, default the name to the file's scenario field (the user can
      // override). On edit the name is immutable, so leave it untouched.
      if (!isEdit && !name.trim()) {
        const derived = deriveMockName(parsed)
        if (derived) {
          setName(derived)
        }
      }
      return false
    },
    onRemove: () => {
      setFile(null)
      setBody(isEdit ? mock?.body ?? null : null)
      return true
    },
  }

  const handleOk = () => {
    if (!body) {
      notification.error({ message: 'Select a file to upload' })
      return
    }
    // On edit the row name is immutable; validate the body against it so a
    // replacement whose `scenario` field diverged is caught before the PUT.
    const expectedName = isEdit && mock ? mock.name : name
    const validationError = validateMockUpload(expectedName, body)
    if (validationError) {
      notification.error({ message: 'Invalid configuration', description: validationError })
      return
    }
    if (isEdit && mock) {
      updateMutation.mutate(buildMockUpdateInput(mock, body), {
        onSuccess: handleClose,
      })
      return
    }
    createMutation.mutate({ name: name.trim(), body }, { onSuccess: handleClose })
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="OK"
      title={isEdit ? 'Edit Mock MCP Configuration' : 'Upload Mock MCP Configuration'}
      width={640}
      confirmLoading={mutation.isLoading}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          <Form.Item
            label="Name"
            style={{ marginBottom: 0 }}
            // The name is the key cases reference; the backend cannot rename a
            // scenario on update, so it is read-only when editing.
            extra={isEdit ? "The body's \"scenario\" field must match this name." : undefined}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Configuration name"
              disabled={isEdit}
            />
          </Form.Item>
        </Form>
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {isEdit ? 'Replace Mock MCP Configuration' : 'Upload Mock MCP Configuration'}
          </p>
          <p className="ant-upload-hint">Single .json file, up to 1 MiB.</p>
        </Upload.Dragger>
      </Space>
    </Modal>
  )
}

export default McpMockUploadModal
